import React, { useRef } from 'react';
import { Box } from '@mui/material';
import { COLOR } from '@src/styles/color';
import {
  AUDIO_ACCEPT,
  MAX_SIZE,
  MIN_DURATION,
  MAX_DURATION,
} from '@src/constants/instantVoiceCloning';

const errorMessages = {
  format: () => ({
    key: 'invalidFormat',
  }),
  size: () => ({
    key: 'invalidSize',
  }),
  tooShort: (name) => ({
    key: 'invalidDurationMin',
    fileName: name,
  }),
  tooLong: (name) => ({
    key: 'invalidDurationMax',
    fileName: name,
  }),
  noAudio: () => ({
    key: 'invalidNoAudio',
  }),
};

const validatorMap = {
  format: ({ file, accept }) => {
    const allowed = accept
      .split(',')
      .map((type) => type.trim().toLowerCase().replace('.', ''));
    const ext = file.name.split('.').pop().toLowerCase();
    if (!allowed.includes(ext)) {
      return errorMessages.format;
    }
    return null;
  },
  size: ({ file, maxSize }) => {
    if (file.size > maxSize) {
      return errorMessages.size;
    }
    return null;
  },
  duration: async ({ file }) =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const audio = new window.Audio();
      audio.src = url;
      audio.addEventListener('loadedmetadata', () => {
        const { duration } = audio;
        if (Number.isNaN(duration) || duration === 0) {
          resolve(errorMessages.noAudio);
        } else if (duration < MIN_DURATION) {
          resolve(errorMessages.tooShort(file.name));
        } else if (duration > MAX_DURATION) {
          resolve(errorMessages.tooLong(file.name));
        } else {
          resolve(null);
        }
        URL.revokeObjectURL(url);
      });
      audio.addEventListener('error', () => {
        resolve(errorMessages.noAudio);
        URL.revokeObjectURL(url);
      });
    }),
  noAudio: async ({ file }) => {
    if (!file) return null;

    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const arrayBuffer = await file.arrayBuffer();

    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const rawData = audioBuffer.getChannelData(0);
    const totalAmplitude = rawData.reduce(
      (sum, value) => sum + Math.abs(value),
      0,
    );
    const averageAmplitude = totalAmplitude / rawData.length;
    if (averageAmplitude < 0.001) {
      // eslint-disable-next-line consistent-return
      return errorMessages.noAudio;
    }
    // eslint-disable-next-line consistent-return
    return null;
  },
};

const isAsyncValidator = (fn) => fn.constructor.name === 'AsyncFunction';

const DropZone = ({
  onDrop,
  accept = AUDIO_ACCEPT,
  maxSize = MAX_SIZE,
  multiple = false,
  disabled = false,
  validatorKeys,
  children,
  onSetError,
  isError,
  ...props
}) => {
  const inputRef = useRef(null);
  const validatorsToUse = validatorKeys
    .map((key) => validatorMap[key])
    .filter(Boolean);

  const validateFile = async (file) => {
    let result = null;
    await validatorsToUse.reduce(async (prevPromise, validator) => {
      await prevPromise;
      if (result !== null) return;
      let validationResult;
      if (isAsyncValidator(validator)) {
        validationResult = await validator({
          file,
          accept,
          maxSize,
          errorMessages,
        });
      } else {
        validationResult = validator({ file, accept, maxSize, errorMessages });
      }
      if (validationResult) {
        result = validationResult;
      }
    }, Promise.resolve());

    return result || null;
  };

  const handleFiles = async (files) => {
    onSetError(null);
    const fileList = Array.from(files);

    const filesToCheck = multiple ? fileList : [fileList[0]];

    const errorMsg = await validateFile(filesToCheck[0]);
    if (errorMsg) {
      onSetError(errorMsg);
      return;
    }

    onSetError('');
    if (onDrop) {
      onDrop(multiple ? filesToCheck : filesToCheck[0]);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  const handleChange = async (e) => {
    if (disabled) return;
    if (e.target.files && e.target.files.length > 0) {
      await handleFiles(e.target.files);
    }
  };

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
      sx={{
        border: isError
          ? `2px dashed ${COLOR.errorDropZone}`
          : `2px dashed ${COLOR.borderDropZone}`,
        borderRadius: 2,
        boxSizing: 'border-box',
        textAlign: 'center',
        backgroundColor: COLOR.backgroundDropZone,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color 0.2s, background-color 0.2s',
        position: 'relative',
        height: props.height,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        ...props.sx,
      }}
      {...props}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={handleChange}
        disabled={disabled}
      />
      {children}
    </Box>
  );
};

export default DropZone;
